import React from "react";
import { Check, CircleNotch, Warning, Download, Play, ArrowsClockwise } from "@phosphor-icons/react";
import { downloadFromUrl } from "@/components/shared";
import PublishPanel from "@/components/PublishPanel";

/**
 * Live progress panel injected below each episode card in Series Studio.
 * Fed by GET /api/series/{id}/video-progress via SeriesStudio's polling
 * useEffect. Renders a step pipeline (queued → script → voice → …), a
 * percentage bar, ETA hint, plus final preview + download when complete
 * or an error + retry when failed.
 */
export default function EpisodeProgressPanel({
  progress,
  episode,
  stepOrder = ["queued", "script", "voice", "talking_photo", "captions", "watermarking", "finalize", "complete"],
  stepLabels = {},
  onRetry,
  onRegenerate,
  onOpenAllVideos,
}) {
  // 1. Cinematic (multi-shot) progress branch. Takes PRECEDENCE over any
  //    stale Talking Head job on the same episode whenever a cinematic render
  //    is active/terminal, so a leftover TH error can't mask it in Cinematic mode.
  const cine = progress?.cinematic_status;
  if (cine || progress?.cinematic_output_url) {
    return <CinematicProgress progress={progress} episode={episode} onRetry={onRetry} onRegenerate={onRegenerate} />;
  }

  if (!progress || !progress.job_id) return null;

  const {
    status, current_step, current_step_label, progress_pct = 0,
    steps_completed = [], output_url, error, watermarked,
  } = progress;

  const isFailed = status === "failed" || status === "cancelled";
  const isDone = status === "completed" && output_url;
  const activeIdx = stepOrder.indexOf(current_step);
  // De-emphasise the watermarking step for paying users (backend skips it,
  // so it never appears in steps_completed for them).
  const visibleSteps = stepOrder.filter(
    (s) => s !== "complete" && (s !== "watermarking" || watermarked || current_step === "watermarking")
  );

  return (
    <div
      className={`rounded-xl border-2 p-4 space-y-3 ${
        isDone ? "bg-emerald-50 border-emerald-200" :
        isFailed ? "bg-red-50 border-red-200" :
        "bg-gradient-to-br from-[#E91E8C]/5 to-white border-[#E91E8C]/30"
      }`}
      data-testid={`episode-progress-${episode.episode_number}`}
    >
      <div className="flex items-center justify-between gap-3">
        <div className="flex items-center gap-2 min-w-0">
          {isDone ? (
            <span className="w-6 h-6 rounded-full bg-emerald-500 text-white flex items-center justify-center flex-shrink-0"><Check size={12} weight="bold" /></span>
          ) : isFailed ? (
            <span className="w-6 h-6 rounded-full bg-red-500 text-white flex items-center justify-center flex-shrink-0"><Warning size={12} weight="fill" /></span>
          ) : (
            <CircleNotch size={16} className="animate-spin text-[#E91E8C] flex-shrink-0" />
          )}
          <span className="text-xs font-semibold text-zinc-900 truncate" data-testid={`episode-progress-label-${episode.episode_number}`}>
            {isDone ? "Video ready" : isFailed ? "Render failed" : (current_step_label || "Rendering")}
          </span>
        </div>
        {!isDone && !isFailed && (
          <span className="text-xs font-bold text-[#E91E8C] flex-shrink-0" data-testid={`episode-progress-pct-${episode.episode_number}`}>
            {progress_pct}%
          </span>
        )}
      </div>

      {/* Progress bar — indeterminate feel when < 100% */}
      {!isFailed && (
        <div className="h-1.5 bg-white rounded-full overflow-hidden border border-zinc-100">
          <div
            className={`h-full ${isDone ? "bg-emerald-500" : "bg-[#E91E8C]"} transition-all duration-500`}
            style={{ width: `${isDone ? 100 : progress_pct}%` }}
          />
        </div>
      )}

      {/* Step pipeline */}
      {!isFailed && (
        <div className="flex items-center gap-1 overflow-x-auto pb-1" data-testid={`episode-steps-${episode.episode_number}`}>
          {visibleSteps.map((s, i) => {
            const done = steps_completed.includes(s) || activeIdx > i;
            const active = current_step === s && !isDone;
            return (
              <div key={s} className="flex items-center gap-1 flex-shrink-0">
                <span
                  className={`text-[10px] font-semibold px-2 py-0.5 rounded-full whitespace-nowrap ${
                    done ? "bg-emerald-100 text-emerald-700" :
                    active ? "bg-[#E91E8C] text-white animate-pulse" :
                    "bg-zinc-100 text-zinc-400"
                  }`}
                >
                  {done ? "✓ " : ""}{stepLabels[s] || s.replace(/_/g, " ")}
                </span>
                {i < visibleSteps.length - 1 && (
                  <span className={`text-[10px] ${done ? "text-emerald-400" : "text-zinc-300"}`}>›</span>
                )}
              </div>
            );
          })}
        </div>
      )}

      {/* Final actions */}
      {isDone && (
        <div className="space-y-3">
          <div className="flex items-center gap-2 pt-1">
            <video
              src={output_url}
              controls
              playsInline
              preload="metadata"
              className="rounded-lg w-24 h-40 object-cover bg-black"
              data-testid={`episode-preview-video-${episode.episode_number}`}
            />
            <div className="flex-1 flex flex-col gap-1.5">
              <button
                onClick={() => downloadFromUrl(output_url, `series-ep${episode.episode_number}.mp4`)}
                className="flex items-center gap-1.5 text-xs font-semibold px-3 py-1.5 rounded-lg bg-zinc-900 text-white hover:bg-zinc-700 w-fit"
                data-testid={`episode-download-${episode.episode_number}`}
              >
                <Download size={12} weight="bold" /> Download MP4
              </button>
              <a
                href={output_url}
                target="_blank"
                rel="noreferrer"
                className="flex items-center gap-1.5 text-xs font-semibold text-[#E91E8C] hover:underline w-fit"
                data-testid={`episode-open-${episode.episode_number}`}
              >
                <Play size={12} weight="fill" /> Open in new tab
              </a>
              {watermarked && (
                <span className="text-[10px] text-zinc-400 italic">Free-tier watermark applied — upgrade to remove</span>
              )}
            </div>
          </div>
          {/* Publish panel — caption generator + platform deep-links */}
          <PublishPanel
            videoUrl={output_url}
            script={episode?.script || episode?.synopsis || episode?.title || ""}
            topic={episode?.title || episode?.hook || ""}
            filename={`series-ep${episode.episode_number}.mp4`}
            compact
          />
        </div>
      )}

      {isFailed && (
        <div className="space-y-2">
          <p className="text-xs text-red-800" data-testid={`episode-progress-error-${episode.episode_number}`}>
            {error || "Render failed — please retry."}
          </p>
          <div className="flex items-center gap-2">
            <button
              onClick={() => onRetry?.(episode.episode_number)}
              className="text-xs font-semibold px-3 py-1.5 rounded-lg bg-red-600 text-white hover:bg-red-700"
              data-testid={`episode-retry-${episode.episode_number}`}
            >
              Retry render
            </button>
            {onOpenAllVideos && (
              <button
                onClick={onOpenAllVideos}
                className="text-xs font-semibold text-zinc-600 hover:text-zinc-900"
              >
                See all my videos →
              </button>
            )}
          </div>
        </div>
      )}
    </div>
  );
}

function CinematicProgress({ progress, episode, onRetry, onRegenerate }) {
  const st = (progress.cinematic_status || "").toLowerCase();
  const url = progress.cinematic_output_url;
  const err = progress.cinematic_error;
  const fallback = progress.fallback_reason;
  if (url && (st === "completed" || st === "partial")) {
    return (
      <div className="rounded-xl border-2 border-emerald-200 bg-emerald-50 p-3 flex items-center gap-2" data-testid={`episode-progress-${episode.episode_number}`}>
        <video src={url} controls playsInline preload="metadata" className="rounded-lg w-20 h-32 object-cover bg-black" data-testid={`cinematic-video-${episode.episode_number}`} />
        <div className="flex flex-col gap-1.5">
          <span className="text-xs font-semibold text-emerald-800">🎬 {st === "partial" ? "Cinematic ready (partial — some shots failed)" : "Cinematic ready"}</span>
          {fallback && <span className="text-[10px] text-amber-700">{fallback}</span>}
          <button
            onClick={() => downloadFromUrl(url, `cinematic-ep${episode.episode_number}.mp4`)}
            className="text-xs font-semibold px-2.5 py-1 rounded bg-zinc-900 text-white w-fit"
          >
            <Download size={11} weight="bold" className="inline mr-1" /> Download
          </button>
          {onRegenerate && (
            <button
              onClick={() => onRegenerate(episode.episode_number)}
              className="text-xs font-semibold px-2.5 py-1 rounded-lg bg-[#E91E8C] text-white w-fit hover:bg-[#d4177d] transition-colors"
              data-testid={`regenerate-cinematic-${episode.episode_number}`}
              title="Not happy with this? Generate a brand-new take."
            >
              <ArrowsClockwise size={11} weight="bold" className="inline mr-1" /> Regenerate
            </button>
          )}
          {st === "partial" && (
            <button onClick={() => onRetry?.(episode.episode_number)} className="text-xs font-semibold px-2.5 py-1 rounded-lg bg-amber-600 text-white w-fit">Retry failed shots</button>
          )}
        </div>
      </div>
    );
  }
  if (st === "failed") {
    const shotErrs = progress.cinematic_shot_errors || [];
    return (
      <div className="rounded-xl border-2 border-red-200 bg-red-50 p-3 space-y-2" data-testid={`episode-progress-${episode.episode_number}`}>
        <p className="text-xs font-semibold text-red-800">🎬 Cinematic render failed</p>
        <p className="text-xs text-red-700" data-testid={`cinematic-error-${episode.episode_number}`}>{err || "Please retry."}</p>
        {shotErrs.length > 0 && (
          <ul className="text-[11px] text-red-600 list-disc pl-4 space-y-0.5" data-testid={`cinematic-shot-errors-${episode.episode_number}`}>
            {shotErrs.slice(0, 6).map((se, i) => <li key={i}>Shot: {se}</li>)}
          </ul>
        )}
        <button
          onClick={() => onRetry?.(episode.episode_number)}
          className="text-xs font-semibold px-3 py-1.5 rounded-lg bg-red-600 text-white"
        >Retry cinematic</button>
      </div>
    );
  }
  return (
    <div className="rounded-xl border-2 border-purple-200 bg-gradient-to-br from-purple-50 to-white p-3 flex items-center gap-2" data-testid={`episode-progress-${episode.episode_number}`}>
      <CircleNotch size={16} className="animate-spin text-purple-600" />
      <span className="text-xs font-semibold text-purple-800">🎬 Rendering cinematic video via Veo… (~2-4 min per shot)</span>
    </div>
  );
}
